import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Vimeo from "@u-wave/react-vimeo";
import { FormattedMessage } from "react-intl";
import { videoEmbedPropTypes } from "../paragraph-video";
import classNames from "classnames";
import useTranslate from "@default/hooks/use-translate";
import PillBorder from "@default/general-components/pill-border";

import PlayIcon from "../../../../../../assets/ui/play_video.svg?url";

const VideoEmbedded = ({
  content,
  customThumbnail,
  ariaLabel = { ariaLabel },
}) => {
  const [showVideo, setShowVideo] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const _t = useTranslate();

  const videoUrl = content?.fieldMediaOembedVideo;

  // if no video url is set, don't render
  if (!videoUrl) return;

  // get the provider from the url
  const getProvider = (url) => {
    const youTubePossibleStrings = ["youtube", "youtu.be"];
    if (youTubePossibleStrings.some((string) => url.includes(string)))
      return "youtube";

    const vimeoPossibleStrings = ["vimeo"];
    if (vimeoPossibleStrings.some((string) => url.includes(string)))
      return "vimeo";
  };

  const provider = getProvider(videoUrl);

  const videoId = videoUrl
    .replace("https://www.youtube.com/watch?v=", "")
    .replace("https://youtu.be/", "");

  const thumbnail = customThumbnail ? customThumbnail : content.thumbnail;

  const mediaClassNames = classNames({
    "embedded-video": true,
    "show-infos": showInfo && !showVideo,
    "hide-infos": !showInfo && showVideo,
    "show-video": showVideo,
    "hide-video": !showVideo,
  });

  return (
    <div className={mediaClassNames}>
      {!showVideo && (
        <>
          <div className="preview">
            {!showVideo && !showInfo && (
              <button
                className="playpause"
                onClick={() => setShowInfo(true)}
                tabIndex={0}
                role={"button"}
                aria-label={` ${_t("accessibility.play_video")} ${ariaLabel}`}
              >
                <PillBorder />
                <img src={PlayIcon} alt={"Abspielen Button"} />
              </button>
            )}
            <div className="dsgvo-thumbnail youtube">
              {thumbnail && (
                <img
                  src={thumbnail.style?.url}
                  alt={thumbnail?.alt || "Video Thumbnail"}
                />
              )}
            </div>
          </div>
          {showInfo && (
            <div className="privacy-info-banner">
              <div className="action-wrapper">
                <div className="accept-info">
                  {provider === "youtube" && (
                    <FormattedMessage id="gdpr.youtube" />
                  )}
                  {provider === "vimeo" && <FormattedMessage id="gdpr.vimeo" />}
                </div>
                <div className="accept-actions">
                  <button
                    tabIndex={0}
                    role="button"
                    aria-label="accept privacy infos"
                    className="btn make-headline main-cta"
                    onClick={() => {
                      setShowVideo(true);
                      setShowInfo(false);
                    }}
                  >
                    <PillBorder blurredBackdrop />
                    <span>
                      <FormattedMessage id="okay" />
                    </span>
                  </button>
                  <Link to={"/datenschutz"} className="btn btn-primary">
                    <FormattedMessage id="learn_more" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {showVideo && (
        <div
          className={`iframe-wrap ${
            provider === "youtube" ? "iframe-16-9 youtube" : "vimeo"
          }`}
        >
          {provider === "youtube" && (
            <iframe
              width="560"
              height="315"
              src={`https://www.youtube-nocookie.com/embed/${videoId}${
                showVideo ? "?autoplay=1" : ""
              }&enablejsapi=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              rel={0}
              allowFullScreen
              className="iframe-youtube"
            />
          )}
          {provider === "vimeo" && (
            <Vimeo
              video={content.fieldMediaOembedVideo}
              responsive
              autoplay
              controls={true}
            />
          )}
        </div>
      )}
    </div>
  );
};

VideoEmbedded.propTypes = {
  content: PropTypes.shape(videoEmbedPropTypes),
};

export default VideoEmbedded;
